import React, { useContext, useEffect, useRef, useState } from 'react';

import { ReactEditor, useSlateStatic } from 'slate-react';

import application from 'application';
import serverInterface from 'server-interface';

import { ReactComponent as EditIcon } from 'assets/images/edit-icon.svg';

import internalEventCodes from 'event-system/codes/internal';

import HelptapEditor from '../../../helpers';

import BaseElement from '../../base';
import GalleryContext from '../context';

import './index.scss';

const CAPTION_UPDATE_DELAY = 600;

function GalleryItem(props) {

  const element = props.element;
  const properties = element.properties || {};

  const context = useContext(GalleryContext);
  const galleryId = properties.galleryId || context.galleryId || null;
  const galleryItemId = properties.galleryItemId || null;

  const editor = useSlateStatic();

  const uploadingReference = useRef(false);
  const captionTimerReference = useRef(null);

  const [rootState, setRootState] = useState({
    blobURL: null,
    uploading: false,
    uploadedPercent: '0%',
    caption: properties.caption || ''
  });

  useEffect(function onLoad() {

    tryUploadRawFile();

    return function onUnload() {
      clearTimeout(captionTimerReference.current);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryId]);

  function getElementPath() {

    return ReactEditor.findPath(editor, props.element);

  }

  function tryUploadRawFile() {

    if (galleryId === null) {
      return;
    }

    if (galleryItemId !== null) {
      return;
    }

    if ((properties.rawFile instanceof File) === false) {
      return;
    }

    uploadRawFile(properties.rawFile);

  }

  async function uploadRawFile(rawFile) {

    if ((rawFile instanceof File) === false) {
      return;
    }

    if (uploadingReference.current === true) {
      return;
    }

    uploadingReference.current = true;

    setRootState(function updateRootState(state) {
      return {
        ...state,
        uploading: true,
        uploadedPercent: '0%',
        blobURL: URL.createObjectURL(rawFile)
      };
    });

    const request = await props.mediaUploader(rawFile);

    request.on('progress', handleUploadProgress);
    request.on('success', handleUploadSuccess);
    request.on('error', handleUploadError);

  }

  async function handleUploadButtonClick() {

    const response = await application.domUtility.askFile({ type: 'image/*' });

    if (response.cancelled === true) {
      return;
    }

    uploadRawFile(response.file);

  }

  async function handleEditButtonClick() {

    const response = await application.domUtility.askFile({ type: 'image/*' });

    if (response.cancelled === true) {
      return;
    }

    replaceRawFile(response.file);

  }

  async function replaceRawFile(rawFile) {

    if ((rawFile instanceof File) === false) {
      return;
    }

    if (uploadingReference.current === true) {
      return;
    }

    uploadingReference.current = true;

    setRootState(function updateRootState(state) {
      return {
        ...state,
        uploading: true,
        uploadedPercent: '0%',
        blobURL: URL.createObjectURL(rawFile)
      };
    });

    const request = await props.mediaUploader(rawFile);

    request.on('progress', handleUploadProgress);
    request.on('success', handleReplaceSuccess);
    request.on('error', handleUploadError);

  }

  async function handleReplaceSuccess(files) {

    const file = files[0];

    try {

      await serverInterface.startup.pages.updateGalleryItemFile(galleryId, galleryItemId, {
        _id: file._id,
        url: file.url,
        name: file.name,
        mimetype: file.mimetype,
        ownerId: file.ownerId
      });

      uploadingReference.current = false;

      setRootState(function updateRootState(state) {
        return {
          ...state,
          uploading: false
        };
      });

      const updatedProperties = {
        ...properties,
        file
      };

      delete updatedProperties.rawFile;

      HelptapEditor.setAttributes(editor, {
        properties: updatedProperties
      }, getElementPath());

    } catch (exception) {

      uploadingReference.current = false;
      handleUploadError();

    }

  }

  function handleUploadProgress(data) {

    let uploadedPercent = Math.floor(data.percent);

    if (uploadedPercent === 100) {
      uploadedPercent = 'finishing up...';
    } else {
      uploadedPercent = `${uploadedPercent}%`;
    }

    setRootState(function updateRootState(state) {
      return {
        ...state,
        uploadedPercent
      };
    });

  }

  async function handleUploadSuccess(files) {

    const file = files[0];

    try {

      const item = await serverInterface.startup.pages.createGalleryItem(galleryId, {
        pageId: props.parentEntityId,
        file: {
          _id: file._id,
          url: file.url,
          name: file.name,
          mimetype: file.mimetype,
          ownerId: file.ownerId
        },
        caption: rootState.caption
      });

      uploadingReference.current = false;

      setRootState(function updateRootState(state) {
        return {
          ...state,
          uploading: false
        };
      });

      HelptapEditor.setAttributes(editor, {
        properties: {
          ...properties,
          file,
          rawFile: null,
          caption: rootState.caption,
          galleryItemId: item._id,
          galleryId
        }
      }, getElementPath());

    } catch (exception) {

      uploadingReference.current = false;
      handleUploadError();

    }

  }

  function handleUploadError() {

    uploadingReference.current = false;

    setRootState(function updateRootState(state) {
      return {
        ...state,
        uploading: false,
        uploadedPercent: '0%',
        blobURL: null
      };
    });

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Failed to upload image. Please try again!',
      seconds: 5,
      type: 'error'
    });

  }

  function handleCaptionChange(event) {

    const caption = event.target.value;

    setRootState(function updateRootState(state) {
      return {
        ...state,
        caption
      };
    });

    HelptapEditor.setAttributes(editor, {
      properties: {
        ...properties,
        caption
      }
    }, getElementPath());

    tryPersistCaption(caption);

  }

  function tryPersistCaption(caption) {

    if (galleryItemId === null) {
      return;
    }

    clearTimeout(captionTimerReference.current);

    captionTimerReference.current = setTimeout(function persistCaption() {

      serverInterface.startup.pages.updateGalleryItemCaption(galleryId, galleryItemId, caption);

    }, CAPTION_UPDATE_DELAY);

  }

  function getImageURL() {

    if (rootState.uploading === true && rootState.blobURL !== null) {
      return rootState.blobURL;
    }

    if (properties.file !== null && typeof properties.file !== 'undefined') {
      return properties.file.url;
    }

    return rootState.blobURL;

  }

  function hasUploadedFile() {

    return properties.file !== null && typeof properties.file !== 'undefined';

  }

  function shouldShowUploadButton() {

    if (hasUploadedFile() === true) {
      return false;
    }

    if (rootState.uploading === true) {
      return false;
    }

    if (rootState.blobURL !== null) {
      return false;
    }

    return true;

  }

  function tryRenderUploadingIndicator() {

    if (rootState.uploading === false) {
      return;
    }

    return <div className="gallery-item-uploading-indicator">
      {rootState.uploadedPercent}
    </div>;

  }

  function tryRenderUploadButton() {

    if (shouldShowUploadButton() === false) {
      return;
    }

    const buttonAttributes = {
      type: 'button',
      className: 'gallery-item-upload-button',
      contentEditable: false,
      onMouseDown(event) {
        event.preventDefault();
      },
      onClick() {
        handleUploadButtonClick();
      }
    };

    return <button {...buttonAttributes}>+ Upload image</button>;

  }

  function renderImage() {

    const imageURL = getImageURL();

    if (imageURL === null) {
      return <div className="gallery-item-placeholder" />;
    }

    return <img className="gallery-item-image" src={imageURL} alt={rootState.caption} />;

  }

  function tryRenderCaptionInput() {

    if (context.captionEnabled === false) {
      return;
    }

    if (shouldShowUploadButton() === true) {
      return;
    }

    const inputAttributes = {
      className: 'gallery-item-caption-input',
      value: rootState.caption,
      placeholder: 'Add caption',
      onChange: handleCaptionChange,
      onMouseDown(event) {
        event.stopPropagation();
      }
    };

    return <input {...inputAttributes} />;

  }

  function tryRenderEditButton() {

    if (hasUploadedFile() === false) {
      return;
    }

    if (rootState.uploading === true) {
      return;
    }

    const editIconAttributes = {
      type: 'button',
      className: 'gallery-item-edit-button',
      contentEditable: false,
      title: 'Replace image',
      onMouseDown(event) {
        event.preventDefault();
      },
      onClick() {
        handleEditButtonClick();
      }
    };

    return <button {...editIconAttributes}>
      <EditIcon />
    </button>;

  }

  function renderImageContainer() {

    return <div className="gallery-item-image-container">
      {renderImage()}
      {tryRenderUploadingIndicator()}
      {tryRenderUploadButton()}
      {tryRenderEditButton()}
    </div>;

  }

  function renderGalleryItemContent() {

    const properties = {
      ...props.attributes,
      contentEditable: false,
      className: 'gallery-item-body caption-' + context.captionPosition + ' caption-' + context.captionAppearance
    };

    if (context.captionPosition === 'top') {
      return <div {...properties}>
        {tryRenderCaptionInput()}
        {renderImageContainer()}
        {props.children}
      </div>;
    }

    return <div {...properties}>
      {renderImageContainer()}
      {tryRenderCaptionInput()}
      {props.children}
    </div>;

  }

  function renderMain() {

    const baseElementProperties = {
      ...props,
      placeholder: '',
      className: 'gallery-item-main'
    };

    return <BaseElement {...baseElementProperties}>
      {renderGalleryItemContent()}
    </BaseElement>;

  }

  return renderMain();

}

export default GalleryItem;